import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { DEFAULT_PRODUCT_OPTION_I18N_INPUT_MODEL } from "./constants/default-input-model";
import { excludeKeys, type ExcludeKeys, type ProductOptionI18nFormModel } from "./type";
import { optionI18nInputZodSchema, type OptionI18nInputZodSchemaType } from "./validation-schema";

import type { ProductOptionI18nFormModelContextType } from "../ui/context/context";

type Props = {
	inputModel: ProductOptionI18nFormModel["InputModel"];
	queryData: ProductOptionI18nFormModel["QueryData"];
};

export const useInitProductOptionI18nFormModelContext = ({
	inputModel: _inputModel,
	queryData,
}: Props): Omit<ProductOptionI18nFormModelContextType, "queryData"> & { resetViewModel: () => void } => {
	const hotelID = _inputModel.hotelID;
	const optionID = _inputModel.optionID;
	const lang = _inputModel.lang;

	const defaultInputModel = DEFAULT_PRODUCT_OPTION_I18N_INPUT_MODEL({ optionID, hotelID, lang, queryData });
	const inputModel = _inputModel ?? defaultInputModel;

	const [viewModel, setViewModel] = useState(
		inputModelToViewModelConverter<
			ProductOptionI18nFormModel["InputModel"],
			ExcludeKeys,
			ProductOptionI18nFormModel["QueryData"],
			OptionI18nInputZodSchemaType
		>({
			defaultInput: defaultInputModel,
			initInput: inputModel,
			currentInput: inputModel,
			excludeKeys: excludeKeys,
			zodSchema: optionI18nInputZodSchema,
		}),
	);

	const onSetViewModel: ProductOptionI18nFormModelContextType["onSetViewModel"] = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce(
					(acc, cur) => {
						acc[cur.key] = cur.value;
						return acc;
					},
					{} as ProductOptionI18nFormModel["ViewModel"],
				),
			} satisfies ProductOptionI18nFormModel["ViewModel"];

			const currentInput = viewModelToInputModelConverter<
				ProductOptionI18nFormModel["InputModel"],
				ExcludeKeys,
				ProductOptionI18nFormModel["QueryData"]
			>(mergedViewModel);

			const newViewModel = inputModelToViewModelConverter<
				ProductOptionI18nFormModel["InputModel"],
				ExcludeKeys,
				ProductOptionI18nFormModel["QueryData"],
				OptionI18nInputZodSchemaType
			>({
				defaultInput: defaultInputModel,
				initInput: inputModel,
				currentInput,
				excludeKeys: excludeKeys,
				zodSchema: optionI18nInputZodSchema,
			});

			setViewModel(newViewModel);
		},
		[defaultInputModel, inputModel, viewModel],
	);

	const resetViewModel = useCallback(() => {
		setViewModel({ ...viewModel, isDirty: false });
	}, [viewModel]);

	return {
		viewModel,
		onSetViewModel,

		resetViewModel,
	};
};
